import { useLinkProps } from "@swan-io/chicane";
import { useRef, useState } from "react";
import { Text as NativeText, StyleSheet, TextProps } from "react-native";
import { Except } from "type-fest";
import { useFocus } from "../hooks/useFocus";
import { useHover } from "../hooks/useHover";
import { usePress } from "../hooks/usePress";
import { colors } from "../utils/design";
import { RouteArea, router } from "../utils/routes";

const styles = StyleSheet.create({
  code: {
    fontFamily: "geist-mono",
    fontWeight: "400",
    fontSize: 13,
    lineHeight: 13,
  },
  miniMono: {
    fontFamily: "geist-mono",
    fontWeight: "400",
    fontSize: 10,
    lineHeight: 10,
  },
  miniAvatar: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 6,
    lineHeight: 6,
  },
  massiveAvatar: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 48,
    lineHeight: 48,
  },
  miniFlat: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 10,
    lineHeight: 10,
  },
  mini: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 10,
    lineHeight: 15,
  },
  smallMono: {
    fontFamily: "geist-mono",
    textTransform: "uppercase",
    fontWeight: "400",
    fontSize: 12,
    lineHeight: 12,
  },
  smallFlat: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 13,
    lineHeight: 13,
  },
  small: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 13,
    lineHeight: 19,
  },
  body: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 15,
    lineHeight: 25,
  },
  bodyFlat: {
    fontFamily: "geist",
    fontWeight: "400",
    fontSize: 15,
    lineHeight: 15,
  },
  title4: {
    fontFamily: "geist-mono",
    textTransform: "uppercase",
    fontWeight: "400",
    fontSize: 13,
    lineHeight: 13,
  },
  title3: {
    fontFamily: "geist",
    fontWeight: "500",
    fontSize: 17,
    lineHeight: 24,
  },
  title2: {
    fontFamily: "geist",
    fontWeight: "500",
    fontSize: 20,
    lineHeight: 26,
  },
  title1: {
    fontFamily: "geist",
    fontWeight: "600",
    fontSize: 32,
    lineHeight: 40,
  },

  ellipsis: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  numeric: {
    fontFeatureSettings: "tnum",
    fontVariantNumeric: "tabular-nums",
  },
});

export type TextVariant =
  | "code"
  | "miniMono"
  | "miniAvatar"
  | "massiveAvatar"
  | "miniFlat"
  | "mini"
  | "smallMono"
  | "smallFlat"
  | "small"
  | "body"
  | "bodyFlat"
  | "title4"
  | "title3"
  | "title2"
  | "title1";

type Props = TextProps & {
  variant?: TextVariant;
  color?: string;
  align?: "left" | "center" | "right";
  ellipsis?: boolean;
  numeric?: boolean;
  textRef?: React.MutableRefObject<NativeText | null>;
};

export const Text: React.FC<Props> = ({
  variant,
  color = colors.neutral[200],
  align,
  ellipsis = false,
  numeric = false,
  style,
  textRef,
  ...props
}) => {
  return (
    <NativeText
      ref={textRef}
      style={[
        variant != null && styles[variant],
        ellipsis && styles.ellipsis,
        numeric && styles.numeric,
        { color, textAlign: align },
        style,
      ]}
      {...props}
    />
  );
};

export const Label: React.FC<Props> = (props) => (
  <Text variant="small" color={colors.neutral[50]} {...props} />
);

type LinkProps = Props & {
  to: string;
  openInNewTab?: boolean;
  replace?: boolean;
  disabled?: boolean;
  activeStyle?: TextProps["style"];
  textRef?: React.MutableRefObject<NativeText | null>;
};

export const Link: React.FC<LinkProps> = ({
  to,
  openInNewTab,
  replace,
  disabled = false,
  style,
  activeStyle,
  textRef,
  onPress: _onPress,
  ...props
}) => {
  const { onClick, active } = useLinkProps({ href: to, replace });

  const onPress: TextProps["onPress"] = (event) => {
    if (openInNewTab) {
      event.preventDefault();
      window.open(to, "_blank");
      return;
    }
    // @ts-expect-error onClick is typed for web, not for react-native-web
    onClick(event);
    _onPress?.(event);
  };

  return (
    <Text
      textRef={textRef}
      href={disabled ? undefined : to}
      onPress={disabled ? undefined : onPress}
      style={[style, active && activeStyle]}
      {...props}
    />
  );
};

type PressableState = {
  hovered: boolean;
  focused: boolean;
  pressed: boolean;
  active: boolean;
};

export type PressableLinkProps = Except<LinkProps, "style" | "children"> & {
  areaName?: RouteArea;
  style: (state: PressableState) => TextProps["style"];
  children: TextProps["children"] | ((state: PressableState) => TextProps["children"]);
};

export const PressableLink: React.FC<PressableLinkProps> = ({
  areaName,
  style,
  children,
  ...props
}) => {
  const route = router.useRoute(areaName != null ? [areaName] : []);
  const isAreaActive = route != null;
  const { active: isRouteActive } = useLinkProps({ href: props.to });
  const active = isAreaActive || isRouteActive;
  const ref = useRef<NativeText>(null);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  useFocus(ref, {
    disabled: props.disabled,
    onFocusChange: setFocused,
  });

  useHover(ref, {
    disabled: props.disabled,
    onHoverChange: setHovered,
  });

  usePress(ref, {
    disabled: props.disabled,
    onPressChange: setPressed,
  });

  return (
    <Link {...props} textRef={ref} style={style({ hovered, focused, pressed, active })}>
      {typeof children === "function" ? children({ hovered, focused, pressed, active }) : children}
    </Link>
  );
};
